// src/components/Dashboard_ADM/GerenciamentoDiplomas.tsx
// UPDATED: 2026-04-27 v4
// ✅ Download ZIP com ficha HTML (print→PDF) + foto PNG
// ✅ Endpoint resolve implementado
// ✅ Cards completos com todos os dados visíveis
// ✅ [NOVO] Aprovação com seleção de destino: Profissional ou Filiado
// Requer: npm install jszip

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import {
  GraduationCap, Search, Download, CheckCircle, XCircle, Clock,
  Loader2, AlertTriangle, RefreshCw, Building2, BookOpen,
  Calendar, Mail, Phone, CreditCard, User, Hash,
  UserCheck, Users, ChevronDown, ChevronUp, Trash2,
} from 'lucide-react';
import apiClient from '../../services/api';
import { glass, text } from './ui/theme';
import GlassBackdrop from './ui/GlassBackdrop';


// ─── Types ───────────────────────────────────────────────────────────────────
type PromoRole = 'professional' | 'affiliate';

interface DiplomaRequest {
  id: string;
  type: 'diploma' | 'segunda-via';
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  resolvedAt?: string;
  // [NOVO] campo preenchido após aprovação com destino
  promotedAs?: PromoRole;
  generatedId?: string;
}

interface StudentDiplomaData {
  studentId:              string;
  studentSystemId:        string;
  studentFullName:        string;
  studentEmail:           string;
  studentPhotoUrl:        string;
  studentCpf:             string;
  studentCin:             string;
  studentPhone:           string;
  studentBirthdate:       string | null;
  studentCourseName:      string;
  studentCourseWorkload:  number | null;
  studentCourseStartDate: string | null;
  studentCourseEndDate:   string | null;
  studentEnrollmentDate:  string | null;
  studentAffiliateId:     string;
  studentAffiliateName:   string;
  diplomaRequests:        DiplomaRequest[];
}

interface CardItem {
  student: StudentDiplomaData;
  request: DiplomaRequest;
  key:     string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
// FIX B2: extrai YYYY-MM-DD como string pura — evita shift de UTC-3 (+1 dia)
const fmt = (d?: string | null): string => {
  if (!d) return '—';
  const part = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '—';
  const [y, m, day] = part.split('-');
  return `${day}/${m}/${y}`;
};

const statusCfg = {
  pending:  { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500',   label: 'Pendente',  Icon: Clock       },
  approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Aprovado',  Icon: CheckCircle },
  rejected: { bg: 'bg-rose-50',   text: 'text-rose-700',   border: 'border-rose-200',   dot: 'bg-rose-500',    label: 'Rejeitado', Icon: XCircle     },
} as const;

// [NOVO] Config visual dos destinos de promoção
const promoCfg: Record<PromoRole, {
  label: string;
  sublabel: string;
  bg: string;
  bgHover: string;
  border: string;
  text: string;
  Icon: React.ElementType;
}> = {
  professional: {
    label: 'Profissional',
    sublabel: 'Gera ID PRO-XXXX',
    bg: 'bg-cosmos-50',
    bgHover: 'hover:bg-cosmos-100',
    border: 'border-cosmos-300',
    text: 'text-cosmos-800',
    Icon: UserCheck,
  },
  affiliate: {
    label: 'Filiado',
    sublabel: 'Gera ID FIL-XXXX',
    bg: 'bg-gold-50',
    bgHover: 'hover:bg-gold-100',
    border: 'border-gold-300',
    text: 'text-gold-800',
    Icon: Users,
  },
};

// ─── Busca imagem como ArrayBuffer + detecta extensão ────────────────────────
const fetchImageData = async (url: string): Promise<{ data: ArrayBuffer; ext: string } | null> => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type') ?? 'image/jpeg';
    const ext = contentType.includes('png') ? 'png'
              : contentType.includes('webp') ? 'webp'
              : 'jpg';
    const data = await res.arrayBuffer();
    return { data, ext };
  } catch { return null; }
};

// ─── Gera HTML da ficha (com foto base64 embutida) ───────────────────────────
const buildHtml = (
  s: StudentDiplomaData,
  req: DiplomaRequest,
  fotoBase64?: string,
  fotoMime?: string,
): string => {
  const statusLabel = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' }[req.status];
  const tipoLabel   = req.type === 'diploma' ? 'Diploma Original' : '2ª Via';
  const promoLabel  = req.promotedAs === 'professional' ? 'Profissional'
                    : req.promotedAs === 'affiliate' ? 'Filiado' : '';

  const fotoHtml = fotoBase64
    ? `<img src="data:${fotoMime};base64,${fotoBase64}" class="foto-img" alt="Foto"/>`
    : `<div class="foto-placeholder">${s.studentFullName.charAt(0)}</div>`;

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"/>
<title>Ficha — ${s.studentFullName}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:40px;max-width:860px;margin:0 auto}
  .header{display:flex;gap:24px;align-items:flex-start;border-bottom:3px solid #1e3a5f;padding-bottom:20px;margin-bottom:24px}
  .foto-img{width:100px;height:120px;object-fit:cover;border-radius:8px;border:2px solid #e5e7eb;flex-shrink:0}
  .foto-placeholder{width:100px;height:120px;border-radius:8px;border:2px solid #e5e7eb;background:#e8f0fe;display:flex;align-items:center;justify-content:center;font-size:40px;font-weight:bold;color:#1e3a5f;flex-shrink:0}
  .header-info h1{font-size:20px;color:#1e3a5f;margin-bottom:4px}
  .sub{font-size:11px;color:#666;margin-bottom:6px}
  .badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:bold;margin-right:4px}
  .pending{background:#fef3c7;color:#92400e}.approved{background:#d1fae5;color:#065f46}.rejected{background:#fee2e2;color:#991b1b}.tipo{background:#ede9fe;color:#5b21b6}.promo-pro{background:#dbeafe;color:#1d4ed8}.promo-fil{background:#f3e8ff;color:#7c3aed}
  .section{margin-bottom:18px}
  .st{font-size:9px;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-bottom:10px}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .f{background:#f9fafb;border-radius:6px;padding:8px 10px}
  .f label{font-size:8px;text-transform:uppercase;color:#9ca3af;font-weight:bold;display:block;margin-bottom:2px}
  .f span{font-size:12px;font-weight:600}
  .assinatura{margin-top:50px;display:flex;justify-content:space-around}
  .al{text-align:center}
  .al-linha{border-top:1px solid #374151;width:200px;padding-top:6px;font-size:11px;color:#374151}
  .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:9px;color:#9ca3af}
  @media print{body{padding:20px}}
</style></head><body>

<div class="header">
  <div>${fotoHtml}</div>
  <div class="header-info">
    <h1>${s.studentFullName}</h1>
    <div class="sub">ID: ${s.studentSystemId} &nbsp;·&nbsp; Matrícula: ${fmt(s.studentEnrollmentDate)}</div>
    <div>
      <span class="badge tipo">${tipoLabel}</span>
      <span class="badge ${req.status}">${statusLabel}</span>
      ${promoLabel ? `<span class="badge ${req.promotedAs === 'professional' ? 'promo-pro' : 'promo-fil'}">${promoLabel}</span>` : ''}
    </div>
    <div style="margin-top:6px;font-size:11px;color:#374151">
     Solicitado em <strong>${fmt(req.requestedAt)}</strong>
${s.studentCourseEndDate ? ` &nbsp;·&nbsp; Formado em <strong>${fmt(s.studentCourseEndDate)}</strong>` : ''}
${req.resolvedAt ? ` &nbsp;·&nbsp; Aprovado pela Federação em <strong>${fmt(req.resolvedAt)}</strong>` : ''}
    </div>
  </div>
</div>

<div class="section">
  <div class="st">Dados Pessoais</div>
  <div class="grid">
    <div class="f"><label>Nome Completo</label><span>${s.studentFullName}</span></div>
    <div class="f"><label>ID do Sistema</label><span>${s.studentSystemId}</span></div>
    <div class="f"><label>E-mail</label><span>${s.studentEmail||'—'}</span></div>
    <div class="f"><label>Telefone</label><span>${s.studentPhone||'—'}</span></div>
    <div class="f"><label>CPF</label><span>${s.studentCpf||'—'}</span></div>
    <div class="f"><label>CIN</label><span>${s.studentCin||'—'}</span></div>
    <div class="f"><label>Data de Nascimento</label><span>${fmt(s.studentBirthdate)}</span></div>
    <div class="f"><label>Data de Matrícula</label><span>${fmt(s.studentEnrollmentDate)}</span></div>
  </div>
</div>

<div class="section">
  <div class="st">Dados do Curso</div>
  <div class="grid">
    <div class="f"><label>Nome do Curso</label><span>${s.studentCourseName||'—'}</span></div>
    <div class="f"><label>Carga Horária</label><span>${s.studentCourseWorkload?s.studentCourseWorkload+'h':'—'}</span></div>
    <div class="f"><label>Data de Início</label><span>${fmt(s.studentCourseStartDate)}</span></div>
    <div class="f"><label>Data de Término</label><span>${fmt(s.studentCourseEndDate)}</span></div>
  </div>
</div>

<div class="section">
  <div class="st">Escola / Filiada</div>
  <div class="grid">
    <div class="f"><label>Escola Credenciada</label><span>${s.studentAffiliateName||'—'}</span></div>
    <div class="f"><label>ID da Escola</label><span>${s.studentAffiliateId||'—'}</span></div>
  </div>
</div>

<div class="assinatura">
  <div class="al"><div class="al-linha">Diretor(a) da Federação</div></div>
  <div class="al"><div class="al-linha">Responsável pela Escola</div></div>
</div>

<div class="footer">
  <span>Federação dos Profissionais da Beleza · Gerado em ${new Date().toLocaleDateString('pt-BR')}</span>
  <span>Solicitação: ${req.id}</span>
</div>
</body></html>`;
};

// ─── Download ZIP ─────────────────────────────────────────────────────────────
const downloadZip = async (
  s: StudentDiplomaData,
  req: DiplomaRequest,
  setDownloading: (id: string | null) => void,
) => {
  setDownloading(req.id);
  try {
    const zip       = new JSZip();
    const nomePasta = `${s.studentSystemId}_${req.type}`;
    const pasta     = zip.folder(nomePasta)!;

    let fotoBase64: string | undefined;
    let fotoMime = 'image/jpeg';
    let fotoExt  = 'jpg';

    if (s.studentPhotoUrl) {
      const imgData = await fetchImageData(s.studentPhotoUrl);
      if (imgData) {
        fotoExt  = imgData.ext;
        fotoMime = `image/${fotoExt === 'jpg' ? 'jpeg' : fotoExt}`;
        pasta.file(`foto_${s.studentSystemId}.${fotoExt}`, imgData.data);
        const bytes = new Uint8Array(imgData.data);
        let binary  = '';
        bytes.forEach(b => binary += String.fromCharCode(b));
        fotoBase64 = btoa(binary);
      }
    }

    const html = buildHtml(s, req, fotoBase64, fotoMime);
    pasta.file(`ficha_${s.studentSystemId}.html`, html);
    pasta.file('INSTRUCOES.txt',
      `FICHA DE DIPLOMA — ${s.studentFullName}\n` +
      `ID: ${s.studentSystemId}\n` +
      `Tipo: ${req.type === 'diploma' ? 'Diploma Original' : '2ª Via'}\n` +
      `Solicitado em: ${fmt(req.requestedAt)}\n\n` +
      `COMO GERAR O PDF:\n` +
      `1. Abra o arquivo ficha_${s.studentSystemId}.html no navegador\n` +
      `2. Pressione Ctrl+P (ou Cmd+P no Mac)\n` +
      `3. Selecione "Salvar como PDF"\n` +
      `4. Clique em Salvar\n`
    );

    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${nomePasta}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('[downloadZip]', err);
    alert('Erro ao gerar o arquivo ZIP. Tente novamente.');
  } finally {
    setDownloading(null);
  }
};

// ─── Campo de dados ───────────────────────────────────────────────────────────
const DataField: React.FC<{ label: string; value?: string | null; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="bg-cosmos-50/60 rounded-xl p-3 border border-cosmos-100/70">
    <p className="text-[9px] font-bold text-cosmos-400 uppercase tracking-wider mb-1 flex items-center gap-1 [&>svg]:text-gold-500">
      {icon}{label}
    </p>
    <p className="text-sm font-semibold text-cosmos-800 break-all">{value || '—'}</p>
  </div>
);

// ─── [NOVO] Painel de seleção de destino de promoção ─────────────────────────
const PromoSelector: React.FC<{
  onSelect: (role: PromoRole) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ onSelect, onCancel, loading }) => (
  <div className="mt-3 p-4 rounded-2xl border border-cosmos-100 bg-cosmos-50/60 space-y-3">
    <p className="text-sm font-semibold text-cosmos-900 flex items-center gap-2">
      <CheckCircle className="w-4 h-4 text-gold-600" />
      Aprovando diploma — selecione o destino:
    </p>
    <div className="grid grid-cols-2 gap-3">
      {(['professional', 'affiliate'] as PromoRole[]).map((role) => {
        const cfg = promoCfg[role];
        return (
          <button
            key={role}
            onClick={() => onSelect(role)}
            disabled={loading}
            className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border font-semibold transition-all
              ${cfg.bg} ${cfg.bgHover} ${cfg.border} ${cfg.text}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading
              ? <Loader2 className="w-5 h-5 animate-spin" />
              : <cfg.Icon className="w-5 h-5" />
            }
            <span className="text-sm">{cfg.label}</span>
            <span className="text-[10px] font-medium opacity-70">{cfg.sublabel}</span>
          </button>
        );
      })}
    </div>
    <button
      onClick={onCancel}
      disabled={loading}
      className="w-full text-xs text-cosmos-400 hover:text-cosmos-600 font-semibold py-1.5 transition-colors"
    >
      ✕ Cancelar
    </button>
  </div>
);

// ─── Card ─────────────────────────────────────────────────────────────────────
const DiplomaCard: React.FC<{
  item:        CardItem;
  processando: string | null;
  downloading: string | null;
  // [ALTERADO] onResolver agora recebe targetRole obrigatório ao aprovar
onResolver:  (item: CardItem, status: 'approved' | 'rejected', targetRole?: PromoRole) => void;
  onDownload:  (item: CardItem) => void;
  onExcluir:   (item: CardItem) => void;
}> = ({ item, processando, downloading, onResolver, onDownload, onExcluir }) => {

  const { student: s, request: req } = item;
  const cfg = statusCfg[req.status] ?? statusCfg.pending;
  const [imgErr, setImgErr]               = useState(false);
  const [showPromoSelect, setShowPromoSelect] = useState(false); // [NOVO]
  const isDownloading = downloading === req.id;
  const isProcessing  = processando === req.id;

  const handleApproveClick = () => {
    setShowPromoSelect(true);
  };

  const handlePromoSelect = (role: PromoRole) => {
    onResolver(item, 'approved', role);
    setShowPromoSelect(false);
  };

  return (
    <div className={`${glass.card} overflow-hidden`}>
      <div className={`h-1 w-full ${cfg.dot}`}/>

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start gap-5 mb-5">
          <div className="flex-shrink-0">
            {s.studentPhotoUrl && !imgErr ? (
              <img src={s.studentPhotoUrl} alt={s.studentFullName} onError={() => setImgErr(true)}
                className="w-24 h-28 rounded-xl object-cover border border-cosmos-100 shadow-sm"/>
            ) : (
              <div className="w-24 h-28 rounded-xl bg-gradient-gold flex items-center justify-center text-white text-4xl font-bold shadow-sm">
                {s.studentFullName.charAt(0)}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-cosmos-900 leading-tight">{s.studentFullName}</h3>
            <p className="text-xs font-mono text-cosmos-400 mt-0.5">{s.studentSystemId}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>{cfg.label}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-cosmos-50 text-cosmos-700 border-cosmos-200">
                <GraduationCap className="w-3.5 h-3.5 text-gold-600"/>
                {req.type === 'diploma' ? 'Diploma Original' : '2ª Via'}
              </span>
              {/* [NOVO] Badge do destino de promoção, visível após aprovação */}
          {req.promotedAs && (() => {
  const cfg      = promoCfg[req.promotedAs!];
  const PromoIcon = cfg.Icon; // PascalCase obrigatório para JSX
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
      text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <PromoIcon className="w-3.5 h-3.5"/>
      {cfg.label}
      {req.generatedId && (
        <span className="font-mono ml-1 opacity-80">· {req.generatedId}</span>
      )}
      {(req as any).registroFepcssp && (
        <span className="font-mono ml-1 opacity-70 text-[10px]">
          #{(req as any).registroFepcssp}
        </span>
      )}
    </span>
  );
})()}
            </div>
            <p className="mt-2 text-xs text-cosmos-400 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5"/>
              Solicitado em {fmt(req.requestedAt)}
              {req.resolvedAt && <> · Resolvido em {fmt(req.resolvedAt)}</>}
            </p>
          </div>
        </div>

        {/* Dados Pessoais */}
        <div className="mb-4">
          <p className={`${text.label} mb-2 flex items-center gap-1.5`}>
            <User className="w-3 h-3 text-gold-500"/>Dados Pessoais
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <DataField label="E-mail"     value={s.studentEmail}               icon={<Mail className="w-3 h-3"/>}/>
            <DataField label="Telefone"   value={s.studentPhone}               icon={<Phone className="w-3 h-3"/>}/>
            <DataField label="CPF"        value={s.studentCpf}                 icon={<CreditCard className="w-3 h-3"/>}/>
            <DataField label="CIN"        value={s.studentCin}                 icon={<Hash className="w-3 h-3"/>}/>
            <DataField label="Nascimento" value={fmt(s.studentBirthdate)}      icon={<Calendar className="w-3 h-3"/>}/>
            <DataField label="Matrícula"  value={fmt(s.studentEnrollmentDate)} icon={<Calendar className="w-3 h-3"/>}/>
          </div>
        </div>

        {/* Dados do Curso */}
        <div className="mb-4">
          <p className={`${text.label} mb-2 flex items-center gap-1.5`}>
            <BookOpen className="w-3 h-3 text-gold-500"/>Curso
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="md:col-span-2">
              <DataField label="Nome do Curso" value={s.studentCourseName} icon={<BookOpen className="w-3 h-3"/>}/>
            </div>
            <DataField label="Carga" value={s.studentCourseWorkload ? `${s.studentCourseWorkload}h` : null}/>
            <DataField label="Escola" value={s.studentAffiliateName} icon={<Building2 className="w-3 h-3"/>}/>
            <DataField label="Início"  value={fmt(s.studentCourseStartDate)} icon={<Calendar className="w-3 h-3"/>}/>
            <DataField label="Término" value={fmt(s.studentCourseEndDate)}   icon={<Calendar className="w-3 h-3"/>}/>
          </div>
        </div>

        {/* Ações */}
        <div className="pt-4 border-t border-cosmos-100/70">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => onDownload(item)} disabled={isDownloading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cosmos-900 text-white text-sm font-semibold hover:bg-cosmos-800 disabled:opacity-60 transition-colors">
              {isDownloading
                ? <><Loader2 className="w-4 h-4 animate-spin"/>Gerando ZIP…</>
                : <><Download className="w-4 h-4"/>Baixar ZIP</>}
            </button>

            {req.status === 'pending' && !showPromoSelect && (
              <>
                {/* [ALTERADO] Aprovar agora abre o seletor de destino */}
                <button
                  onClick={handleApproveClick}
                  disabled={isProcessing}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  Aprovar
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>

                <button
                  onClick={() => onResolver(item, 'rejected')}
                  disabled={isProcessing}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100 disabled:opacity-50 transition-colors"
                >
                  <XCircle className="w-4 h-4" /> Rejeitar
                </button>
              </>
            )}

           {req.status !== 'pending' && (
  <div className="text-xs text-cosmos-400 italic space-y-0.5">
    <p>
      {req.status === 'approved'
        ? `✅ Aprovado${req.promotedAs ? ` como ${promoCfg[req.promotedAs].label}` : ''} em ${fmt(req.resolvedAt)}`
        : `❌ Rejeitado em ${fmt(req.resolvedAt)}`
      }
      {req.generatedId && <> · <span className="font-mono text-cosmos-500">{req.generatedId}</span></>}
    </p>
    {req.status === 'approved' && s.studentCourseEndDate && (
      <p className="text-gold-600 font-medium not-italic">
        🎓 Formado em: {fmt(s.studentCourseEndDate)}
      </p>
    )}
  </div>
)}
            {req.status !== 'pending' && (
              <button
                onClick={() => onExcluir(item)}
                disabled={processando === req.id}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 text-rose-500 text-sm font-semibold hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50 transition-colors ml-auto"
              >
                <Trash2 className="w-4 h-4" /> Excluir
              </button>
            )}
          </div>

          {/* [NOVO] Painel de seleção de destino — aparece ao clicar Aprovar */}
          {req.status === 'pending' && showPromoSelect && (
            <PromoSelector
              onSelect={handlePromoSelect}
              onCancel={() => setShowPromoSelect(false)}
              loading={isProcessing}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const GerenciamentoDiplomas: React.FC = () => {
  const [items, setItems]               = useState<CardItem[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [busca, setBusca]               = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [processando, setProcessando]   = useState<string | null>(null);
  const [downloading, setDownloading]   = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data: any = await apiClient.get('/students/diploma-requests/all');
      const students: StudentDiplomaData[] = data.data ?? [];

      const flat: CardItem[] = [];
      for (const s of students) {
        for (const req of s.diplomaRequests) {
          flat.push({ student: s, request: req, key: `${s.studentId}-${req.id}` });
        }
      }

      flat.sort((a, b) => {
        if (a.request.status === 'pending' && b.request.status !== 'pending') return -1;
        if (a.request.status !== 'pending' && b.request.status === 'pending') return 1;
        return new Date(b.request.requestedAt).getTime() - new Date(a.request.requestedAt).getTime();
      });

      setItems(flat);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar solicitações');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // [ALTERADO] handleResolver agora recebe targetRole opcional
  // Ao aprovar, targetRole é obrigatório (Profissional ou Filiado)
  // Ao rejeitar, targetRole é undefined
  const handleResolver = async (
    item: CardItem,
    novoStatus: 'approved' | 'rejected',
    targetRole?: PromoRole,
  ) => {
    const destLabel = targetRole ? promoCfg[targetRole].label : '';
    const confirmMsg = novoStatus === 'approved'
      ? `Aprovar diploma de ${item.student.studentFullName} como ${destLabel}?`
      : `Rejeitar diploma de ${item.student.studentFullName}?`;

    if (!confirm(confirmMsg)) return;

    setProcessando(item.request.id);
    try {
      // [ALTERADO] payload inclui targetRole quando aprovando
     const payload: Record<string, unknown> = { status: novoStatus };
if (novoStatus === 'approved' && targetRole) {
  payload.targetRole = targetRole;
  // Envia a data de conclusão do curso para o backend usar como
  // "Profissional desde" — e NÃO a data em que o admin aprovou
  if (item.student.studentCourseEndDate) {
    payload.courseEndDate = item.student.studentCourseEndDate;
  }
}

      const response: any = await apiClient.post(
        `/students/${item.student.studentId}/diploma-requests/${item.request.id}/resolve`,
        payload,
      );

      const resolvedAt  = new Date().toISOString();
      // [NOVO] generatedId retornado pelo backend (ID PRO-XXXX ou FIL-XXXX)
      const generatedId = response?.data?.generatedId ?? response?.generatedId;

      setItems(prev => prev.map(i =>
        i.key === item.key
          ? {
              ...i,
              request: {
                ...i.request,
                status: novoStatus,
                resolvedAt,
                promotedAs: novoStatus === 'approved' ? targetRole : undefined,
                generatedId: generatedId ?? undefined,
              },
            }
          : i
      ));
    } catch (err) {
      console.warn('[resolve]', err);
      alert('Erro ao processar solicitação. Tente novamente.');
    } finally {
      setProcessando(null);
    }
  };

  const handleDownload = (item: CardItem) => {
    downloadZip(item.student, item.request, setDownloading);
  };
const handleExcluir = async (item: CardItem) => {
    const confirmMsg = `Excluir definitivamente a solicitação de ${item.student.studentFullName}?\n\nEsta ação não pode ser desfeita.`;
    if (!confirm(confirmMsg)) return;

    setProcessando(`del-${item.request.id}`);
    try {
      await apiClient.delete(
        `/students/${item.student.studentId}/diploma-requests/${item.request.id}`,
      );
      setItems(prev => prev.filter(i => i.key !== item.key));
    } catch (err) {
      console.warn('[excluir]', err);
      alert('Erro ao excluir solicitação. Tente novamente.');
    } finally {
      setProcessando(null);
    }
  };
  const filtrados = useMemo(() => items.filter(i => {
    if (filtroStatus !== 'todos' && i.request.status !== filtroStatus) return false;
    if (busca.trim()) {
      const q = busca.toLowerCase();
      return i.student.studentFullName.toLowerCase().includes(q) ||
             i.student.studentSystemId.toLowerCase().includes(q) ||
             i.student.studentCourseName.toLowerCase().includes(q) ||
             i.student.studentAffiliateName.toLowerCase().includes(q);
    }
    return true;
  }), [items, filtroStatus, busca]);

  const stats = useMemo(() => ({
    total:    items.length,
    pendente: items.filter(i => i.request.status === 'pending').length,
    aprovado: items.filter(i => i.request.status === 'approved').length,
    rejeitado:items.filter(i => i.request.status === 'rejected').length,
  }), [items]);

  return (
    <div className="space-y-6 relative">
      <GlassBackdrop />
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-cosmos-900">Solicitações de Diploma</h1>
          <p className="text-cosmos-400 mt-1">Visualize, aprove (definindo destino) e baixe fichas completas com foto e dados do aluno</p>
        </div>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cosmos-200 text-cosmos-700 font-semibold hover:bg-cosmos-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/> Atualizar
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total',      value: stats.total,     Icon: GraduationCap },
          { label: 'Pendentes',  value: stats.pendente,  Icon: Clock         },
          { label: 'Aprovados',  value: stats.aprovado,  Icon: CheckCircle   },
          { label: 'Rejeitados', value: stats.rejeitado, Icon: XCircle       },
        ].map(s => (
          <div key={s.label} className="bg-cosmos-50/60 rounded-2xl p-5 border border-cosmos-100/70">
            <s.Icon className="w-4 h-4 text-gold-600 mb-2"/>
            <p className="text-2xl font-semibold text-cosmos-900">{s.value}</p>
            <p className={text.label}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* FILTROS */}
      <div className={`${glass.card} p-4 flex flex-wrap gap-4 items-center`}>
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cosmos-300"/>
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por aluno, curso ou escola…"
            className="w-full pl-11 pr-4 py-2.5 border border-cosmos-200 rounded-xl text-sm focus:ring-2 focus:ring-gold-400 focus:border-transparent"/>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'todos',    label: 'Todos',      count: null            },
            { value: 'pending',  label: 'Pendentes',  count: stats.pendente  },
            { value: 'approved', label: 'Aprovados',  count: stats.aprovado  },
            { value: 'rejected', label: 'Rejeitados', count: stats.rejeitado },
          ].map(f => (
            <button key={f.value} onClick={() => setFiltroStatus(f.value)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                filtroStatus === f.value
                  ? 'bg-cosmos-900 text-white border-cosmos-900'
                  : 'bg-white text-cosmos-600 border-cosmos-200 hover:border-gold-400'
              }`}>
              {f.label}{f.count !== null && <span className="ml-1.5 text-xs opacity-70">({f.count})</span>}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-gold-500 animate-spin"/></div>}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-rose-600 flex-shrink-0"/>
          <div><p className="font-semibold text-rose-900">Erro ao carregar</p><p className="text-rose-700 text-sm">{error}</p></div>
        </div>
      )}

      {!loading && !error && (
        filtrados.length === 0 ? (
          <div className={`${glass.card} border-dashed py-20 text-center`}>
            <GraduationCap className="w-12 h-12 text-cosmos-200 mx-auto mb-4"/>
            <p className="text-cosmos-600 font-semibold text-lg">Nenhuma solicitação encontrada</p>
            <p className="text-cosmos-400 text-sm mt-1">
              {busca || filtroStatus !== 'todos' ? 'Ajuste os filtros' : 'Ainda não há solicitações de diploma'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-cosmos-400 font-semibold">
              {filtrados.length} solicitação{filtrados.length !== 1 ? 'ões' : ''}
            </p>
            {filtrados.map(item => (
              <DiplomaCard
                key={item.key}
                item={item}
                processando={processando}
                downloading={downloading}
              onResolver={handleResolver}
                onDownload={handleDownload}
                onExcluir={handleExcluir}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default GerenciamentoDiplomas;